import {
  Box,
  Card,
  Flex,
  Grid,
  Skeleton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  useDisclosure,
  useToast,
  Button,
  Icon,
  Input,
} from '@chakra-ui/react'
import { keepPreviousData, useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getDocuments, deleteDocument, deleteHrDocument, deleteTemplateDocument, updateDocument } from '../../api/document/document'
import { CheckCircleFilledIcon, ClockFillIcon, Trash04Icon, PlusIcon } from '../../assets/icons'
import formatDate from '../../helpers/formatDate'
import { AuthContext } from '../../hooks/auth/useAuth'
import { useRole } from '../../hooks/auth/useRole'
import { statusColors } from '../../themes/components/tag'
import { DocumentType } from '../../types/document/documentTypes'
import DocumentCard from '../cards/DocumentCard'
import handleToast from '../HandleToast'
import AddSignedDocumentModal from '../modals/addDocumentSigned'
import DiscardModal from '../modals/discardModal'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useParams } from 'react-router-dom'
import { Edit05Icon } from '../../assets/icons'
import { CircleFlag } from 'react-circle-flags'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import AvatarCell from './base/AvatarCell'
import AddDocumentModal from '../modals/addDocument'
import AddDocumentFromTemplateModal from '../modals/addDocumentFromTemplate'
import ListGridSwitcher from '../input/ListGridSwich'
import ViewSwitch from '../input/DocumentContentSwitch'
import { fetchEmployeeList } from '../../api/workforce/workforce'

interface TableComponentProps {
  docType: 'hr' | 'templates' | 'documents'
  recipient?: string
  viewing?: 'content' | 'documents';
  setViewing?: (view: 'content' | 'documents') => void;
  isUserProfile?: boolean;
  showAddButton?: boolean;
  onAddClick?: (file?: File) => void;
}

interface DocumentRow {
  created_at: string
  file: string
  id: string
  type: string
  title: string
  signed_file: string
  status: string
  author: {
    id: string
    first_name: string
    last_name: string
    avatar: string
    email: string
  }
  recipient: {
    id: string
    first_name: string
    last_name: string
    avatar: string
    email: string
  }
}
export const DocumentTable: React.FC<TableComponentProps> = ({ docType, recipient, viewing, setViewing, isUserProfile = false, showAddButton = false, onAddClick }) => {
  const { t } = useTranslation()
  const { user } = useContext(AuthContext)
  const { isAdmin } = useRole()
  const toast = useToast()
  const queryClient = useQueryClient()
  const params = useParams()
  const [selectedTemplate, setSelectedTemplate] = useState<Record<string, unknown> | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isAddFromTemplateOpen, setIsAddFromTemplateOpen] = useState(false)
  const [initialFile, setInitialFile] = useState<File | null>(null)

  const handleEditClick = (document: Record<string, unknown>) => {
    setSelectedTemplate(document)
    onOpen()
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => updateDocument(id, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      handleToast(toast, true, t('common.states.success'), t('messages.updateSuccess'))
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('common.states.error'), error?.message || t('messages.updateFailed'))
    },
  })

  const EditableTitle: React.FC<{ row: RowData }> = ({ row }) => {
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState(row?.title ?? '')

    useEffect(() => {
      setValue(row?.title ?? '')
    }, [row?.title])

    const save = (e?: React.FocusEvent | React.KeyboardEvent) => {
      if (e && 'stopPropagation' in e) e.stopPropagation()
      if (value === (row?.title ?? '')) {
        setEditing(false)
        return
      }
      updateMutation.mutate({ id: row.id.toString(), title: value })
      setEditing(false)
    }

    return editing ? (
      <Input
        size="sm"
        value={value}
        autoFocus
        onChange={(e) => { e.stopPropagation(); setValue(e.target.value) }}
        onBlur={(e) => save(e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save(e)
          if (e.key === 'Escape') {
            e.stopPropagation()
            setValue(row?.title ?? '')
            setEditing(false)
          }
        }}
        width="100%"
      />
    ) : (
      <Text
        textStyle="l-xs"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          e.preventDefault()
          setEditing(true)
        }}
        cursor="pointer"
        isTruncated
        maxW="280px"
        title={row?.title || ''}
        _hover={{ color: 'blue.500', textDecoration: 'underline' }}
      >
        {row?.title || t('common.labels.na')}
      </Text>
    )
  }

  const columnsTemplates: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('documents.fields.documentName'),
      accessor: 'title',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => <EditableTitle row={row} />
    },
    {
      label: t('documents.fields.dateCreated'),
      accessor: 'created_at',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.created_at)}</Text>,
    },
    {
      label: t('common.fields.author'),
      accessor: 'author',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={`${row.author?.first_name || ''} ${row.author?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('common.fields.country'),
      accessor: 'country',
      type: 'custom',
      Cell: ({ row }) => {
        if (!row.country || row.country === '' || row.country === 'all' || row.country === 'ALL') {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        // Check if it's a valid 2-letter country code
        const isValidCountryCode = /^[A-Za-z]{2}$/.test(row.country)
        const countryName = getCountryNameByCode(row.country.toUpperCase())

        // If not a valid country code or country name returns invalid, show "All Countries"
        if (!isValidCountryCode || !countryName || countryName === '?' || countryName === row.country.toUpperCase()) {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        return (
          <Flex alignItems="center" gap="4px">
            <CircleFlag
              countryCode={row.country.toLowerCase()}
              style={{ width: '16px', height: '16px' }}
            />
            <Text textStyle="l-xs">
              {countryName}
            </Text>
          </Flex>
        )
      }
    },
    {
      label: t('common.fields.version'),
      accessor: 'version',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.version || t('common.labels.na')}</Text>,
    },
    {
      label: t('table.actions'),
      accessor: 'actions',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            handleEditClick(row)
          }}
          ml={4}
          width="74px"
          height="32px"
          variant={'actions'}
        >
          <Icon width="14px" height="14px" fill="none" color="solid.500" mr="8px">
            <Edit05Icon />
          </Icon>
          <Text textStyle="l-xs">{t('common.actions.edit')}</Text>
        </Button>
      ),
    },
  ]

  const columnsHr: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('documents.fields.documentName'),
      accessor: 'title',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => <EditableTitle row={row} />
    },
    {
      label: t('documents.fields.dateCreated'),
      accessor: 'created_at',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.created_at)}</Text>,
    },
    {
      label: t('common.fields.author'),
      accessor: 'author',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={`${row.author?.first_name || ''} ${row.author?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('table.actions'),
      accessor: 'actions',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            handleEditClick(row)
          }}
          ml={4}
          width="74px"
          height="32px"
          variant={'actions'}
        >
          <Icon width="14px" height="14px" fill="none" color="solid.500" mr="8px">
            <Edit05Icon />
          </Icon>
          <Text textStyle="l-xs">{t('common.actions.edit')}</Text>
        </Button>
      ),
    },
  ]

  const restrictedColumnsHrInfo: Column[] = [
    { label: t('documents.fields.documentName'), accessor: 'title', type: 'string' },
    {
      label: t('common.fields.author'),
      accessor: 'author',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={row.author ? `${row.author.first_name ?? ''} ${row.author.last_name ?? ''}`.trim() || '—' : '—'} />
      ),
    },
    {
      label: t('common.fields.lastUpdated'),
      accessor: 'updated_at',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.updated_at)}</Text>,
    },
  ]

  const columnsDocument: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('documents.fields.documentName'),
      accessor: 'title',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => <EditableTitle row={row} />
    },
    {
      label: t('common.fields.recipient'),
      accessor: 'recipient',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.recipient?.profile_picture} name={`${row.recipient?.first_name || ''} ${row.recipient?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('common.fields.author'),
      accessor: 'author',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={`${row.author?.first_name || ''} ${row.author?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('documents.fields.dateCreated'),
      accessor: 'created_at',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.created_at)}</Text>,
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const waiting = row.status === 'pending_signing' || row.status === 'pending_digital_signature'
        const done = row.status === 'signed' || row.status === 'digitally_signed'
        const alreadySigned = row.status === 'already_signed'
        const label = waiting
          ? t('documents.status.waiting')
          : done
            ? t('documents.status.signed')
            : alreadySigned
              ? t('documents.status.alreadySigned')
              : t('documents.status.live')
        return (
          <Tag
            borderRadius="14px"
            h="28px"
            color={waiting ? statusColors.warning.color : statusColors.success.color}
            bg={waiting ? statusColors.warning.bg : statusColors.success.bg}
            borderColor={waiting ? statusColors.warning.border : statusColors.success.border}
            borderWidth="1px"
            whiteSpace="nowrap"
          >
            <TagLeftIcon>{waiting ? <ClockFillIcon /> : <CheckCircleFilledIcon />}</TagLeftIcon>
            <TagLabel textStyle="h-ov">{label}</TagLabel>
          </Tag>
        )
      },
    },
    {
      label: t('common.fields.version'),
      accessor: 'version',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.version || t('common.labels.na')}</Text>,
    },
    {
      label: t('common.fields.country'),
      accessor: 'country',
      type: 'custom',
      Cell: ({ row }) => {
        if (!row.country || row.country === '' || row.country === 'all' || row.country === 'ALL') {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        // Check if it's a valid 2-letter country code
        const isValidCountryCode = /^[A-Za-z]{2}$/.test(row.country)
        const countryName = getCountryNameByCode(row.country.toUpperCase())

        // If not a valid country code or country name returns invalid, show "All Countries"
        if (!isValidCountryCode || !countryName || countryName === '?' || countryName === row.country.toUpperCase()) {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        return (
          <Flex alignItems="center" gap="4px">
            <CircleFlag
              countryCode={row.country.toLowerCase()}
              style={{ width: '16px', height: '16px' }}
            />
            <Text textStyle="l-xs">
              {countryName}
            </Text>
          </Flex>
        )
      }
    },
    {
      label: '',
      accessor: 'actions',
      type: 'actions',
      Cell: ({ row }: { row: RowData }) => {
        const isSignatory = user?.id === row.recipient?.id

        return (
          <Flex gap={2}>
            {isSignatory && (row.status === 'pending_signing' || row.status === 'pending_digital_signature') && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setCurrentDocument(row.id)
                  openUploadSignature()
                }}
                variant={'actions'}
              >
                <Icon width="14px" height="14px" fill="none" color="solid.500" mr="8px">
                  <Edit05Icon />
                </Icon>
                <Text textStyle="l-xs">
                  {row.status === 'pending_digital_signature'
                    ? t('documents.actions.signDigitally')
                    : t('documents.actions.signAndUpload')}
                </Text>
              </Button>
            )}
          </Flex>
        )
      },
    },
  ]

  const hrForMeColumns: Column[] = [
    { label: t('documents.fields.documentName'), accessor: 'title', type: 'string' },
    {
      label: t('documents.fields.assignedSignatory'),
      accessor: 'title',
      type: 'string',
      Cell: () => <Text textStyle="l-xs">{t('documents.labels.assignedToMe')}</Text>,
    },
    {
      label: t('common.fields.recipient'),
      accessor: 'recipient',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={`${row.author?.first_name || ''} ${row.author?.last_name || ''}`.trim()} />
      ),
    },
  ]

  const userDocumentsColumns: Column[] = [
    {
      label: t('documents.fields.documentName'),
      accessor: 'title',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => <EditableTitle row={row} />
    },
    {
      label: t('common.fields.recipient'),
      accessor: 'recipient',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.recipient?.profile_picture} name={`${row.recipient?.first_name || ''} ${row.recipient?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('common.fields.author'),
      accessor: 'author',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.author?.profile_picture} name={`${row.author?.first_name || ''} ${row.author?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('documents.fields.dateCreated'),
      accessor: 'created_at',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.created_at)}</Text>,
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const waiting = row.status === 'pending_signing' || row.status === 'pending_digital_signature'
        const done = row.status === 'signed' || row.status === 'digitally_signed'
        const alreadySigned = row.status === 'already_signed'
        const label = waiting
          ? t('documents.status.waiting')
          : done
            ? t('documents.status.signed')
            : alreadySigned
              ? t('documents.status.alreadySigned')
              : t('documents.status.live')
        return (
          <Tag
            borderRadius="14px"
            h="28px"
            color={waiting ? statusColors.warning.color : statusColors.success.color}
            bg={waiting ? statusColors.warning.bg : statusColors.success.bg}
            borderColor={waiting ? statusColors.warning.border : statusColors.success.border}
            borderWidth="1px"
            whiteSpace="nowrap"
          >
            <TagLeftIcon>{waiting ? <ClockFillIcon /> : <CheckCircleFilledIcon />}</TagLeftIcon>
            <TagLabel textStyle="h-ov">{label}</TagLabel>
          </Tag>
        )
      },
    },
    {
      label: t('common.fields.version'),
      accessor: 'version',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.version || t('common.labels.na')}</Text>,
    },
    {
      label: t('common.fields.country'),
      accessor: 'country',
      type: 'custom',
      Cell: ({ row }) => {
        if (!row.country || row.country === '' || row.country === 'all' || row.country === 'ALL') {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        // Check if it's a valid 2-letter country code
        const isValidCountryCode = /^[A-Za-z]{2}$/.test(row.country)
        const countryName = getCountryNameByCode(row.country.toUpperCase())

        // If not a valid country code or country name returns invalid, show "All Countries"
        if (!isValidCountryCode || !countryName || countryName === '?' || countryName === row.country.toUpperCase()) {
          return <Text textStyle="l-xs">{t('common.labels.allCountries')}</Text>
        }

        return (
          <Flex alignItems="center" gap="4px">
            <CircleFlag
              countryCode={row.country.toLowerCase()}
              style={{ width: '16px', height: '16px' }}
            />
            <Text textStyle="l-xs">
              {countryName}
            </Text>
          </Flex>
        )
      }
    },
    {
      label: '',
      accessor: 'actions',
      type: 'actions',
      Cell: ({ row }: { row: RowData }) => {
        const isSignatory = user?.id === row.recipient?.id

        return (
          <Flex gap={2}>
            {isSignatory && (row.status === 'pending_signing' || row.status === 'pending_digital_signature') && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setCurrentDocument(row.id)
                  openUploadSignature()
                }}
                variant={'actions'}
              >
                <Icon width="14px" height="14px" fill="none" color="solid.500" mr="8px">
                  <Edit05Icon />
                </Icon>
                <Text textStyle="l-xs">
                  {row.status === 'pending_digital_signature'
                    ? t('documents.actions.signDigitally')
                    : t('documents.actions.signAndUpload')}
                </Text>
              </Button>
            )}
          </Flex>
        )
      },
    },
  ]

  const currentTab = params?.type ?? 'documents'

  const renderedColumns = isAdmin
    ? docType === 'documents'
      ? columnsDocument
      : docType === 'hr'
        ? columnsHr
        : columnsTemplates
    : currentTab === 'hrinfo' || docType === 'hr'
      ? restrictedColumnsHrInfo
      : docType === 'documents'
        ? userDocumentsColumns
        : hrForMeColumns

  const { isOpen: isUploadSignatureOpen, onOpen: openUploadSignature, onClose: closeUploadSignature } = useDisclosure()

  const [currentDocument, setCurrentDocument] = useState<string | null>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [selectedDocumentsRows, setSelectedDocumentsRows] = useState<(string | number)[]>([])
  const [selectedHrRows, setSelectedHrRows] = useState<(string | number)[]>([])
  const [selectedTemplatesRows, setSelectedTemplatesRows] = useState<(string | number)[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const filtersKey = JSON.stringify(filters)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      setInitialFile(files[0])
      setIsAddModalOpen(true)
    }
  }

  const getSelectedRows = () => {
    switch (docType) {
      case 'hr':
        return selectedHrRows
      case 'templates':
        return selectedTemplatesRows
      default:
        return selectedDocumentsRows
    }
  }

  const setSelectedRows = (rows: (string | number)[]) => {
    switch (docType) {
      case 'hr':
        setSelectedHrRows(rows)
        break
      case 'templates':
        setSelectedTemplatesRows(rows)
        break
      default:
        setSelectedDocumentsRows(rows)
        break
    }
  }

  useEffect(() => {
    setPage(1)
  }, [docType])

  const documents = useQuery({
    queryKey: ['documents',
      page,
      searchTerm,
      docType,
      filtersKey,
      recipient
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return getDocuments({
        search: searchTerm,
        page,
        page_size: itemsPerPage,
        doc_type: docType,
        filters,
        recipient: recipient ?? undefined,
        fields:
          'id,title,recipient.id,recipient.first_name,recipient.last_name,recipient.profile_picture,author.first_name,author.last_name,author.profile_picture,status,version,country,created_at,updated_at,file,signed_file,type',
      })
    },
  })

  const totalItems = documents.data?.count ?? 0
  const computedMaxPage = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const apiMaxPage = typeof documents.data?.max_page === 'number' && documents.data.max_page > 0
    ? documents.data.max_page
    : undefined
  const effectiveMaxPage = apiMaxPage ? Math.min(apiMaxPage, computedMaxPage) : computedMaxPage

  // Prefetch next page for snappier pagination
  useEffect(() => {
    const nextPage = page + 1
    if (effectiveMaxPage <= page) return
    try {
      queryClient.prefetchQuery({
        queryKey: ['documents', nextPage, searchTerm, docType, filtersKey, recipient],
        queryFn: () =>
          getDocuments({
            search: searchTerm,
            page: nextPage,
            page_size: itemsPerPage,
            doc_type: docType,
            filters,
            recipient: recipient ?? undefined,
            // request only the fields our table renders
            fields:
              'id,title,recipient.id,recipient.first_name,recipient.last_name,recipient.profile_picture,author.first_name,author.last_name,author.profile_picture,status,version,country,created_at,updated_at,file,signed_file,type',
          }),
        staleTime: 30 * 1000,
      })
    } catch (err) {
      // ignore prefetch errors
    }
  }, [page, searchTerm, docType, filters, filtersKey, recipient, queryClient, effectiveMaxPage])

  const deleteMutation = useMutation({
    mutationFn: async (documentIds: string[]) => {
      // Delete all documents in parallel
      const deletePromises = documentIds.map((documentId) => {
        if (docType === 'hr') {
          return deleteHrDocument(documentId)
        } else if (docType === 'templates') {
          return deleteTemplateDocument(documentId)
        } else {
          return deleteDocument(documentId)
        }
      })
      return Promise.all(deletePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setSelectedRows([])
      setShowDiscardModal(false)
    },
  })

  const handleDelete = () => {
    const selectedRows = getSelectedRows()
    deleteMutation.mutate(selectedRows.map(id => id.toString()))
  }

  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('list')

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const currentSelected = getSelectedRows()
    if (isChecked) {
      setSelectedRows([...currentSelected, id])
    } else {
      setSelectedRows(currentSelected.filter((rowId) => rowId !== id))
    }
  }

  const currentPageRowIds = (documents.data?.data || []).map((row: RowData) => row.id)
  const selectedRows = getSelectedRows()
  const allSelected =
    (documents.data?.data?.length || 0) > 0 && currentPageRowIds.every((id: number) => selectedRows.includes(id))
  const isIndeterminate =
    (documents.data?.data?.length || 0) > 0 &&
    currentPageRowIds.some((id: number) => selectedRows.includes(id)) &&
    !allSelected

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows([...getSelectedRows(), ...currentPageRowIds.filter((id: string | number) => !getSelectedRows().includes(id))])
    } else {
      setSelectedRows(getSelectedRows().filter((id) => !currentPageRowIds.includes(id)))
    }
  }

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  // --- Filter infrastructure (replaces TableSearch) ---

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // Infinite scroll author filter
  const [authorFilterSearch, setAuthorFilterSearch] = useState('')
  const authorFilterQuery = useInfiniteQuery({
    queryKey: ['filter-doc-authors', 10, authorFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: authorFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
  })
  const authorFilterList = useMemo(
    () =>
      (authorFilterQuery.data?.pages ?? []).flatMap((p: { data?: Array<{ id: string; first_name: string; last_name: string }> }) =>
        Array.isArray(p?.data)
          ? p.data.map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))
          : [],
      ),
    [authorFilterQuery.data?.pages],
  )

  // Infinite scroll recipient filter
  const [recipientFilterSearch, setRecipientFilterSearch] = useState('')
  const recipientFilterQuery = useInfiniteQuery({
    queryKey: ['filter-doc-recipients', 10, recipientFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: recipientFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    enabled: !isUserProfile && docType !== 'hr' && docType !== 'templates',
  })
  const recipientFilterList = useMemo(
    () =>
      (recipientFilterQuery.data?.pages ?? []).flatMap((p: { data?: Array<{ id: string; first_name: string; last_name: string }> }) =>
        Array.isArray(p?.data)
          ? p.data.map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))
          : [],
      ),
    [recipientFilterQuery.data?.pages],
  )

  // Static filter options
  const countryFilterList = useMemo(() => phoneNumbers.map(c => ({ id: c.code, name: c.name })), [])
  const documentStatusOptions = useMemo(() => [
    { id: 'no_signing', name: t('documents.statusOptions.noSigningRequired') },
    { id: 'pending_signing', name: t('documents.statusOptions.pendingSigning') },
    { id: 'signed', name: t('documents.status.signed') },
    { id: 'already_signed', name: t('documents.status.alreadySigned') },
  ], [t])

  // Determine which filters are visible based on docType
  const showRecipientFilter = !isUserProfile && docType !== 'hr' && docType !== 'templates' && isAdmin
  const showStatusFilter = docType === 'documents'
  const showCountryFilter = docType === 'templates' || docType === 'documents'

  return (
    <>
      <Card
        width="100%"
        p="0"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <AddSignedDocumentModal
          isOpen={isUploadSignatureOpen}
          onClose={closeUploadSignature}
          document={currentDocument ?? ''}
        />
        <Grid>
          <Box>
            <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px" minW={0}>
              <SearchInput onSearch={handleSearch} />
              <Flex gap="8px" mr="20px">
                {getSelectedRows().length > 0 ? (
                  <Button onClick={() => setShowDiscardModal(true)} 
                    variant="error" 
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="13px"
                    fontWeight="600"
                    borderRadius="8px"
                  >
                    <Icon fill="none" color="error.100" mr={2}>
                      <Trash04Icon />
                    </Icon>
                    {t('common.modals.actionCountTitle', {
                      action: t('common.actions.delete'),
                      count: getSelectedRows().length,
                      item: t('common.labels.row', { count: getSelectedRows().length }),
                    })}
                  </Button>
                ) : (
                  <>
                    {viewing !== undefined && setViewing !== undefined && (
                      <ViewSwitch viewing={viewing} setViewing={setViewing} />
                    )}
                    <ListGridSwitcher
                      isGrid={selectedView === 'grid'}
                      onToggle={() => setSelectedView(selectedView === 'grid' ? 'list' : 'grid')}
                    />
                    <FilterButton
                      label={t('common.fields.author')}
                      options={authorFilterList}
                      onApply={(selected) => updateFilter('author', selected)}
                      search={authorFilterSearch}
                      setSearch={setAuthorFilterSearch}
                      onLoadMore={() => authorFilterQuery.fetchNextPage()}
                      hasMore={!!authorFilterQuery.hasNextPage}
                      isLoadingMore={authorFilterQuery.isFetchingNextPage}
                      isLoading={authorFilterQuery.isFetching && !authorFilterQuery.isFetchingNextPage}
                    />
                    {showRecipientFilter && (
                      <FilterButton
                        label={t('common.fields.recipient')}
                        options={recipientFilterList}
                        onApply={(selected) => updateFilter('recipient', selected)}
                        search={recipientFilterSearch}
                        setSearch={setRecipientFilterSearch}
                        onLoadMore={() => recipientFilterQuery.fetchNextPage()}
                        hasMore={!!recipientFilterQuery.hasNextPage}
                        isLoadingMore={recipientFilterQuery.isFetchingNextPage}
                        isLoading={recipientFilterQuery.isFetching && !recipientFilterQuery.isFetchingNextPage}
                      />
                    )}
                    {showStatusFilter && (
                      <FilterButton
                        label={t('common.fields.status')}
                        options={documentStatusOptions}
                        onApply={(selected) => {
                          // Expand pending_signing to include both pending_signing and pending_digital_signature
                          const expanded = selected.flatMap(v =>
                            v === 'pending_signing' ? ['pending_signing', 'pending_digital_signature'] : [v]
                          )
                          updateFilter('status', expanded)
                        }}
                      />
                    )}
                    {showCountryFilter && (
                      <FilterButton
                        label={t('common.fields.country')}
                        options={countryFilterList}
                        onApply={(selected) => updateFilter('country', selected)}
                      />
                    )}
                    {showAddButton && (
                      <Button 
                        variant="filter" 
                        bg="black" 
                        color="white" 
                        onClick={() => {
                          if (onAddClick) {
                            onAddClick()
                          } else if (docType === 'documents') {
                            setIsAddFromTemplateOpen(true)
                          } else {
                            setIsAddModalOpen(true)
                          }
                        }} 
                        gap="8px"
                        size="sm"
                        h="32px"
                        px="14px"
                        fontSize="13px"
                        fontWeight="600"
                        borderRadius="8px"
                      >
                        <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                          <PlusIcon />
                        </Icon>
                        {t('common.actions.add')}
                      </Button>
                    )}
                  </>
                )}
              </Flex>
            </Flex>

            <Box minHeight="58vh">
              {selectedView === 'grid' && documents.isLoading && (
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap="16px" p="12px 20px">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} h="320px" borderRadius="16px" />
                  ))}
                </Box>
              )}
              {selectedView === 'grid' && documents.data && (
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap="16px" p="12px 20px">
                  {(documents.data?.data || []).map(
                    (document: DocumentRow) => {
                      return (
                        <DocumentCard
                          type={document.type as DocumentType}
                          title={document.title}
                          timestamp={document.created_at}
                          document={document.status === 'signed' ? document.signed_file : document.file}
                          author={{
                            name: `${document?.author?.first_name} ${document?.author?.last_name}`,
                            avatar: document?.author?.avatar,
                          }}
                          onTagClick={() => {
                            if ((document.status === 'pending_signing' || document.status === 'pending_digital_signature') && user?.id === document.recipient.id) {
                              setCurrentDocument(document.id)
                              openUploadSignature()
                            } else {
                              handleToast(
                                toast,
                                false,
                                t('common.states.error'),
                                t('documents.messages.onlyRecipientMaySign'),
                              )
                            }
                          }}
                          canSign={user?.id === document?.recipient?.id}
                          signatureDone={
                            document.status === 'signed'
                              ? true
                              : document.status === 'pending_signing'
                                ? false
                                : undefined
                          }
                        />
                      )
                    },
                  )}
                </Box>
              )}
              {selectedView === 'list' && documents.isLoading && <LoadingTable columns={renderedColumns} />}
              {selectedView === 'list' && documents.data && (
                <MasterTable
                  columns={renderedColumns}
                  data={documents.data?.data || []}
                  onRowClick={(row) => window.open(row.status === 'signed' ? row.signed_file : row.file, '_blank')}
                  selectedRows={getSelectedRows()}
                  onSelectAll={handleSelectAll}
                  onSelectRow={handleSelectRow}
                  allSelected={allSelected}
                  isIndeterminate={isIndeterminate}
                  tableType="documents"
                />
              )}
            </Box>
          </Box>
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={effectiveMaxPage}
            totalItems={totalItems}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Grid>
        <DiscardModal
          show={showDiscardModal}
          handleClose={() => setShowDiscardModal(false)}
          handleDelete={handleDelete}
          action="delete"
          selectedRowsCount={getSelectedRows().length}
        />
      </Card>

      {/* Edit Modal */}
      {selectedTemplate && (
        <AddDocumentModal
          isOpen={isOpen}
          onClose={() => {
            onClose()
            setSelectedTemplate(null)
          }}
          type={docType === 'documents' ? 'document' : docType === 'hr' ? 'hr' : 'template'}
          editMode={true}
          existingDocument={selectedTemplate}
        />
      )}


      <AddDocumentModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          setInitialFile(null)
        }}
        type={docType === 'documents' ? 'document' : docType === 'hr' ? 'hr' : 'template'}
        assigned_to={recipient}
        initialFile={initialFile}
      />

      <AddDocumentFromTemplateModal
        isOpen={isAddFromTemplateOpen}
        onClose={() => setIsAddFromTemplateOpen(false)}
        docType="documents"
      />
    </>
  )
}

export default DocumentTable
